import React, { useState } from 'react'
import AdminModalSidebar from './AdminModalSidebar';
import AdminModalHeader from './AdminModalHeader';
import AdminModalFooter from './AdminModalFooter';
import GroupDetail from './details/GroupDetail';
import UserDetail from './details/UserDetail';
import UsersTableView from './views/UsersTableView';
import GroupCreateFormView from './views/GroupCreateFormView';
import AccessControlPanelView from './views/AccessControlPanelView';
import GroupsTableView from './views/GroupsTableView';
import CatalogueTableView from './views/CatalogueTableView';
import CsvReviewView from './views/CsvReviewView';
import { Suspense } from 'react';
import { Spinner } from '../ui/spinner';
import {
    Dialog,
    DialogOverlay,
    DialogPortal,
} from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';

export type AdminCatalogueView = 'CATALOGUE' | 'GROUPS' | 'GROUP_DETAIL' | 'GROUP_CREATE' | 'USERS' | 'USER_DETAIL' | 'ACCESS_CONTROL' | 'CSV_REVIEW';

export interface SelectedSample {
    id: string;
    sample: {
        title: string;
        accessControl: {
            allowedGroups: { _id: string; name: string }[];
            allowedUsers: { sub: string; username: string }[];
        };
        isPrivate: boolean;
    };
}

interface ICatalogue {
    open: boolean;
    onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
}

const Catalogue = ({open, onOpenChange}: ICatalogue) => {
    const { t } = useTranslation();
    const [view, setView] = useState<AdminCatalogueView>('CATALOGUE');
    const [targetId, setTargetId] = useState<string | null>(null); // For Detail views
    // Initialized here bc needs to be passed down to 2 sibling views
    const [selectedSamples, setSelectedSamples] = useState<SelectedSample[]>([]);
    // CSV review state: matched samples + unmatched identifiers from CSV upload
    const [csvReviewData, setCsvReviewData] = useState<{ matched: SelectedSample[]; unmatched: string[] } | null>(null);

    // Helper to jump into details
    const navigateToDetail = (view: AdminCatalogueView, id: string) => {
        setTargetId(id);
        setView(view);
    };

    const getViewTitle = (view: AdminCatalogueView): string => {
        const titles: Record<AdminCatalogueView, string> = {
            'CATALOGUE': t('adminCatalogue.common.catalogue'),
            'GROUPS': t('adminCatalogue.common.groups'),
            'GROUP_DETAIL': targetId ? t('adminCatalogue.titles.groupDetails') : t('adminCatalogue.titles.createGroup'),
            'GROUP_CREATE': t('adminCatalogue.titles.createGroup'),
            'USERS': t('adminCatalogue.common.users'),
            'USER_DETAIL': t('adminCatalogue.titles.userDetails'),
            'ACCESS_CONTROL': t('adminCatalogue.titles.accessControl'),
            'CSV_REVIEW': t('adminCatalogue.titles.csvReview'),
        };
        return titles[view];
    };



    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogPortal>
                <DialogOverlay />
                <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
                    <div className="bg-background border rounded-lg shadow-lg w-[90vw] max-w-6xl h-[80vh] flex overflow-hidden pointer-events-auto"
                    data-testid="admin-catalogue-dialog"
                    >
                        <AdminModalSidebar view={view} onNavigate={setView} />

                        <main className="flex flex-col flex-1 min-w-0">
                            <AdminModalHeader
                                view={view}
                                title={getViewTitle(view)}
                                showBackButton={view === 'USER_DETAIL' || view === 'CSV_REVIEW'}
                                onBack={() => setView(
                                    view === 'USER_DETAIL' ?
                                    'USERS' : view === 'GROUP_DETAIL' ?
                                    'GROUPS' : 'CATALOGUE'
                                )}
                                onClose={() => onOpenChange(false)}
                            />

                            {/* Dynamic Body */}
                            <section className="flex-1 overflow-auto p-4">
                               <Suspense 
                                    fallback={
                                    <div className="flex h-full w-full items-center justify-center">
                                        <Spinner fontSize={15}/>
                                    </div>
                                    }
                                >
                                    {view === 'CATALOGUE' && (
                                        <CatalogueTableView
                                          setView={setView}
                                          selectedSamples={selectedSamples}
                                          setSelectedSamples={setSelectedSamples}
                                          onCsvReview={(data) => {
                                            setCsvReviewData(data);
                                            setView('CSV_REVIEW');
                                          }}
                                        />
                                    )}
                                    {view === 'GROUPS' && (
                                        <GroupsTableView
                                            onEdit={(id) => navigateToDetail('GROUP_DETAIL', id)}
                                            onCreate={() => {
                                                setTargetId(null);
                                                setView('GROUP_DETAIL');
                                            }}
                                        />
                                    )}
                                    {view === 'GROUP_DETAIL' && (
                                        <GroupDetail
                                            id={targetId}
                                            onBack={() => setView('GROUPS')}
                                        />
                                    )}
                                    {view === 'GROUP_CREATE' && <GroupCreateFormView />}
                                    {view === 'USERS' && <UsersTableView onEdit={(id) => navigateToDetail('USER_DETAIL', id)} />}
                                    {view === 'USER_DETAIL' && <UserDetail id={targetId} />}
                                    {view === 'CSV_REVIEW' && csvReviewData && (
                                        <CsvReviewView
                                            matched={csvReviewData.matched}
                                            unmatched={csvReviewData.unmatched}
                                            onCancel={() => {
                                                setCsvReviewData(null);
                                                setView('CATALOGUE');
                                            }}
                                            onProceed={() => {
                                                setSelectedSamples(csvReviewData.matched);
                                                setCsvReviewData(null);
                                                setView('ACCESS_CONTROL');
                                            }}
                                        />
                                    )}
                                    {view === 'ACCESS_CONTROL' && (
                                        <AccessControlPanelView
                                            selectedSamples={selectedSamples}
                                            setSelectedSamples={setSelectedSamples}
                                            setView={setView}
                                        />
                                    )}
                                </Suspense>
                            </section>

                            {/* Dynamic Footer */}
                            {/* <AdminModalFooter view={view} /> */}
                        </main>
                    </div>
                </div>
            </DialogPortal>
        </Dialog>
    )
}

export default Catalogue